/**
 * LoginBannerEditor — admin tab for managing the one-shot welcome /
 * migration banner shown at the top of the public login page.
 *
 * Backed by `GET/PUT /api/config/login-banner` (public GET, admin PUT).
 * The default copy (in DEFAULT_LOGIN_BANNER in routes/config.py)
 * explains the Yii1 → new-site migration and points users to the
 * "Forgot password" flow when their stored password no longer auto-
 * fills. When the customer base has rolled over, an admin toggles
 * `enabled` off and the banner disappears site-wide.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Save, Info, AlertTriangle, CheckCircle2, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VARIANTS = [
  { id: 'info',    label: 'Info (blue)',     icon: Info,           color: 'text-cyan-700  bg-cyan-50  border-cyan-200' },
  { id: 'success', label: 'Success (green)', icon: CheckCircle2,   color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  { id: 'warning', label: 'Warning (amber)', icon: AlertTriangle,  color: 'text-amber-700 bg-amber-50 border-amber-200' },
];

export default function LoginBannerEditor({ token }) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/config/login-banner`);
      const data = await r.json();
      setDraft({
        enabled:     !!data.enabled,
        variant:     data.variant || 'info',
        title:       data.title   || '',
        body:        data.body    || '',
        dismissible: data.dismissible !== false,
      });
    } catch (e) {
      setMsg({ type: 'error', text: 'Could not load banner config' });
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load — same data-fetch pattern used by all the other
  // admin editors in this folder (LegacySyncPanel, InvoiceTemplateEditor
  // etc.).
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!draft) return;
    setSaving(true); setMsg({ type: '', text: '' });
    try {
      const r = await fetch(`${API_URL}/api/config/login-banner`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify(draft),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      setMsg({ type: 'success', text: 'Saved. Visit the login page to verify.' });
    } catch (e) {
      setMsg({ type: 'error', text: e.message || 'Save failed' });
    } finally { setSaving(false); }
  };

  if (loading || !draft) {
    return (
      <div className="p-8 flex items-center gap-2 text-slate-500" data-testid="login-banner-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  const variantCfg = VARIANTS.find((v) => v.id === draft.variant) || VARIANTS[0];
  const VariantIcon = variantCfg.icon;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="login-banner-editor">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Login page banner</h2>
          <p className="text-sm text-slate-600 mt-1">
            Banner shown at the top of the public login page. Use it for
            site-update / migration announcements. Toggle <b>Enabled</b> off
            once everyone has migrated.
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="login-banner-save"
          className="px-4 py-2 rounded-lg text-sm font-bold bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-md disabled:opacity-50 flex items-center gap-1.5"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </header>

      {msg.text && (
        <div
          className={`px-3 py-2 rounded-lg text-sm ${
            msg.type === 'success'
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border border-rose-200 text-rose-800'
          }`}
          data-testid={`login-banner-msg-${msg.type}`}
        >
          {msg.text}
        </div>
      )}

      {/* Enabled toggle */}
      <label className="flex items-center justify-between gap-3 p-4 bg-white border border-slate-200 rounded-xl cursor-pointer">
        <div>
          <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
            {draft.enabled ? <Eye className="w-4 h-4 text-emerald-600" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
            Show banner on login page
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            When off, the login page renders without any announcement.
          </p>
        </div>
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
          data-testid="login-banner-enabled"
          className="w-5 h-5 accent-cyan-600"
        />
      </label>

      {/* Variant */}
      <div className="space-y-2">
        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide">Colour theme</label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {VARIANTS.map((v) => {
            const Icon = v.icon;
            const active = draft.variant === v.id;
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => setDraft({ ...draft, variant: v.id })}
                data-testid={`login-banner-variant-${v.id}`}
                className={`px-3 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 ${
                  active ? `${v.color} ring-2 ring-cyan-500 ring-offset-1` : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {v.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Title */}
      <div className="space-y-1.5">
        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide">Title</label>
        <input
          type="text"
          value={draft.title}
          maxLength={200}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
          data-testid="login-banner-title"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
          placeholder="Welcome to the new AUTO-REMAP.COM"
        />
      </div>

      {/* Body */}
      <div className="space-y-1.5">
        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide">Body text</label>
        <textarea
          value={draft.body}
          maxLength={4000}
          rows={8}
          onChange={(e) => setDraft({ ...draft, body: e.target.value })}
          data-testid="login-banner-body"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 font-mono leading-relaxed"
          placeholder="Use blank lines to separate paragraphs…"
        />
        <p className="text-[11px] text-slate-500">
          Plain text. Blank lines start a new paragraph. Max 4000 characters.
        </p>
      </div>

      {/* Dismissible */}
      <label className="flex items-center justify-between gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer">
        <div>
          <div className="text-sm font-medium text-slate-900">Allow users to dismiss</div>
          <p className="text-xs text-slate-500 mt-0.5">
            Adds a small ✕ button. Choice is remembered in the browser
            until the banner text changes.
          </p>
        </div>
        <input
          type="checkbox"
          checked={draft.dismissible}
          onChange={(e) => setDraft({ ...draft, dismissible: e.target.checked })}
          data-testid="login-banner-dismissible"
          className="w-5 h-5 accent-cyan-600"
        />
      </label>

      {/* Live preview */}
      <div className="space-y-2">
        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide">Preview</label>
        <div
          className={`p-4 border rounded-xl ${variantCfg.color}`}
          data-testid="login-banner-preview"
        >
          <div className="flex items-start gap-3">
            <VariantIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              {draft.title && (
                <h3 className="font-bold text-sm leading-tight">{draft.title}</h3>
              )}
              {draft.body && (
                <div className="text-xs leading-relaxed mt-1 whitespace-pre-line">
                  {draft.body}
                </div>
              )}
            </div>
            {draft.dismissible && <span className="text-base leading-none opacity-60">✕</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
